import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Send, Square, FileText } from 'lucide-react';
import { useOrganization, useClerk } from '@clerk/clerk-react';
import { useWorkspace } from '../context/WorkspaceContext';
import MessageList from './MessageList';
import LoadingIndicator from './LoadingIndicator';
import SaveReportModal from './SaveReportModal';
import { getWebSocketUrl, getApiBaseUrl, getOrgSlug } from '../config/backend.config';

const ChatInterface = forwardRef(({ user, onConnectionChange, selectedWorkspace, onSaveReport, currentModel, onModelChange, onClearSessionRequest, selectedReferenceFiles = [], onReportProgress, onProcessingChange }, ref) => {
  const { organization } = useOrganization();
  const clerk = useClerk();
  const workspace = useWorkspace();
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [messageToConvert, setMessageToConvert] = useState(null);
  const [activeGenerationJob, setActiveGenerationJob] = useState(null); // { jobId, reportName }

  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const connectionDebounceRef = useRef(null);
  const organizationRef = useRef(organization);
  const selectedWorkspaceRef = useRef(selectedWorkspace);

  useEffect(() => {
    organizationRef.current = organization;
    selectedWorkspaceRef.current = selectedWorkspace;
  }, [organization, selectedWorkspace]);

  const getAuthToken = useCallback(() => clerk.session?.getToken(), [clerk.session]);

  // Expose sendClearCommand, sendCompactCommand, sendExploreCommand, clearMessages, openSaveDialog, and clearActiveJob to parent component
  useImperativeHandle(ref, () => ({
    sendClearCommand: () => sendClearCommand(),
    sendCompactCommand: () => sendCompactCommand(),
    sendExploreCommand: (command) => sendExploreCommand(command),
    clearMessages: () => {
      setMessages([]);
      setShowSaveDialog(false);
      setMessageToConvert(null);
    },
    openSaveDialog: () => {
      if (messages.length === 0) {
        alert('No messages to save. Start a conversation first.');
        return;
      }
      setMessageToConvert(null);
      setShowSaveDialog(true);
    },
    clearActiveGenerationJob: () => {
      setActiveGenerationJob(null);
    }
  }));

  // Scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    // Use setTimeout to ensure DOM has updated
    setTimeout(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ 
          behavior: 'smooth',
          block: 'end',
          inline: 'nearest'
        });
      }
    }, 100);
  };

  // Debounced connection change to prevent flickering
  const debouncedConnectionChange = (connected) => {
    if (connectionDebounceRef.current) {
      clearTimeout(connectionDebounceRef.current);
    }
    
    connectionDebounceRef.current = setTimeout(() => {
      setIsConnected(connected);
      onConnectionChange(connected);
    }, connected ? 100 : 500); // Shorter delay for connection, longer for disconnection
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Notify parent when processing state changes (for auto-save)
  useEffect(() => {
    if (onProcessingChange) {
      onProcessingChange(isProcessing);
    }
  }, [isProcessing]); // eslint-disable-line react-hooks/exhaustive-deps

  // WebSocket connection - connect when workspace is selected
  useEffect(() => {
    // Only connect if workspace is selected
    if (!selectedWorkspace?.workspace_id || !user || !organization?.id || !clerk?.session) {
      return;
    }

    const workspaceId = selectedWorkspace.workspace_id;
    const model = selectedWorkspace.model || currentModel || 'haiku';

    const connectWebSocket = async () => {
      try {
        // Get Clerk session token for authentication
        const sessionToken = await clerk.session.getToken();
        if (!sessionToken) {
          console.error('Unable to get authentication token for WebSocket');
          return;
        }

        // Get organization-specific backend URL
        const orgSlug = getOrgSlug(organizationRef.current);
        const baseWsUrl = getWebSocketUrl(orgSlug);

        // Workspace WebSocket URL: /ws/chat/{workspace_id}?token={token}&model={model}
        const wsUrl = `${baseWsUrl.replace('/ws', '')}/ws/chat/${encodeURIComponent(workspaceId)}?token=${encodeURIComponent(sessionToken)}&model=${encodeURIComponent(model)}`;

        console.log('Connecting to workspace chat:', wsUrl.replace(sessionToken, 'TOKEN_HIDDEN'));

        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          console.log('Workspace chat connected');
          // Notify WorkspaceContext that this workspace is now connected
          workspace.markConnected(workspaceId);
          debouncedConnectionChange(true);
          setLoadingMessage('');
        };

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            handleWebSocketMessage(data);
          } catch (error) {
            console.error('Error parsing WebSocket message:', error);
          }
        };

        ws.onclose = (event) => {
          console.log('Workspace chat disconnected', { code: event.code, reason: event.reason });
          // Notify WorkspaceContext that this workspace is now disconnected
          workspace.markDisconnected(workspaceId);
          debouncedConnectionChange(false);

          // Don't auto-reconnect if server closed due to idle timeout
          if (event.code === 1000 && event.reason === 'Idle timeout') {
            console.log('Server closed due to idle timeout. Refresh the page to reconnect.');
            return;
          }

          console.log('WebSocket closed unexpectedly. Refresh the page to reconnect.');
        };

        ws.onerror = (error) => {
          console.error('Workspace chat error:', error);
          debouncedConnectionChange(false);
        };
      } catch (error) {
        console.error('Failed to connect workspace chat:', error);
        debouncedConnectionChange(false);
      }
    };

    connectWebSocket();

    return () => {
      if (connectionDebounceRef.current) {
        clearTimeout(connectionDebounceRef.current);
        connectionDebounceRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [selectedWorkspace?.workspace_id, selectedWorkspace?.model, user, organization?.id, onConnectionChange]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleWebSocketMessage = (data) => {
    switch (data.type) {
      case 'agent_message':
        setMessages(prev => [...prev, {
          id: Date.now() + Math.random(),
          type: 'agent',
          content: data.content,
          timestamp: data.timestamp,
          can_save_report: data.can_save_report || false,
          files: data.files || null  // Inline file attachments with s3_key for lazy URL generation
        }]);
        setIsProcessing(false);
        setIsLoading(false);
        setLoadingMessage('');
        // Scroll to bottom when agent responds
        scrollToBottom();
        break;
        
      case 'status_update':
        // Update loading message instead of adding to chat
        setLoadingMessage(data.content);
        setIsLoading(true);
        // Sync isProcessing state with backend processing state
        if (data.is_processing !== undefined) {
          setIsProcessing(data.is_processing);
        } else {
          // If is_processing not provided, assume true when status is shown
          setIsProcessing(true);
        }
        break;
        
      case 'status_delete':
        // Clear loading indicator
        setIsLoading(false);
        setLoadingMessage('');
        // Note: Don't set isProcessing to false here - let agent_message or error handle it
        // This allows cancel button to remain visible during cleanup
        break;
        
      case 'file_attachment':
        setMessages(prev => [...prev, {
          id: Date.now() + Math.random(),
          type: 'file',
          filename: data.filename,
          fileType: data.file_type,
          fileSize: data.file_size,
          fileData: data.file_data,
          timestamp: data.timestamp
        }]);
        // Scroll to bottom for file attachments
        scrollToBottom();
        break;
        
      case 'file_url':
        setMessages(prev => [...prev, {
          id: Date.now() + Math.random(),
          type: 'file_url',
          filename: data.filename,
          fileType: data.file_type,
          fileSize: data.file_size,
          fileUrl: data.file_url,
          mimeType: data.mime_type,
          isPublic: data.is_public,
          timestamp: data.timestamp,
          can_save_report: data.can_save_report || false
        }]);
        // Scroll to bottom for file URLs
        scrollToBottom();
        break;
        
      case 'pong':
        // Pong response (ping/pong no longer used for keep-alive)
        break;
        
      case 'system':
        // System messages (like idle timeout notifications) - logged but not shown in chat
        console.log('System message:', data.content);
        break;
        
      case 'error':
        setMessages(prev => [...prev, {
          id: Date.now() + Math.random(),
          type: 'error',
          content: data.content,
          timestamp: new Date().toISOString()
        }]);
        setIsProcessing(false);
        setIsLoading(false);
        setLoadingMessage('');
        // Scroll to bottom for error messages
        scrollToBottom();
        break;
        
      case 'timeout_with_retry':
        setMessages(prev => [...prev, {
          id: Date.now() + Math.random(),
          type: 'timeout_with_retry',
          content: data.content,
          timestamp: data.timestamp || new Date().toISOString(),
          retryAvailable: data.retry_available
        }]);
        setIsProcessing(false);
        setIsLoading(false);
        setLoadingMessage('');
        // Scroll to bottom for timeout messages
        scrollToBottom();
        break;

      case 'report_progress':
        // Forward report progress events to parent
        if (onReportProgress) {
          onReportProgress(data);
        }
        // Clear active generation job when it completes or is cancelled
        if (data.status === 'complete' || data.status === 'cancelled') {
          if (activeGenerationJob && activeGenerationJob.jobId === data.job_id) {
            setActiveGenerationJob(null);
          }
        }
        break;

      default:
        console.log('Unknown message type:', data.type);
    }
  };

  const sendMessage = () => {
    if (!inputValue.trim() || !isConnected || isProcessing) return;

    // Build message content with file locations attached
    let messageContent = inputValue.trim();
    
    // Attach file locations to the message content if files are selected
    if (selectedReferenceFiles && selectedReferenceFiles.length > 0) {
      const fileLocations = selectedReferenceFiles.map(file => {
        const filePath = file.file_path || `references/${file.filename}`;
        return `File: ${file.filename} (Location: ${filePath})`;
      }).join('\n');
      
      messageContent += `\n\n--- Referenced Files ---\n${fileLocations}`;
    }

    // Workspace message format with file references in content and metadata
    const message = {
      type: 'user_message',
      content: messageContent
    };

    // Also include file references as metadata for backend processing
    if (selectedReferenceFiles && selectedReferenceFiles.length > 0) {
      message.file_references = selectedReferenceFiles.map(file => ({
        file_id: file.file_id,
        filename: file.filename,
        file_path: file.file_path
      }));
    }

    // Add user message to UI immediately (show original content without file locations)
    setMessages(prev => [...prev, {
      id: Date.now(),
      type: 'user',
      content: inputValue.trim(),
      timestamp: new Date().toISOString(),
      fileReferences: selectedReferenceFiles || []
    }]);

    // Send to WebSocket
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
      setIsProcessing(true);
      setIsLoading(true);
      setLoadingMessage('Processing message...');
    }

    setInputValue('');

    // Reset textarea height
    if (fileInputRef.current) {
      fileInputRef.current.style.height = 'auto';
    }

    // Scroll to bottom immediately after sending
    scrollToBottom();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Auto-resize textarea
  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    
    // Auto-resize textarea
    const textarea = e.target;
    textarea.style.height = 'auto';
    textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
  };

  const handleInterrupt = () => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'interrupt',
        user_id: user
      }));
      setIsProcessing(false);
      setIsLoading(false);
      setLoadingMessage('');
    }
  };

  const handleRetry = () => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN && !isProcessing) {
      wsRef.current.send(JSON.stringify({
        type: 'retry',
        user_id: user
      }));
      setIsProcessing(true);
      setIsLoading(true);
      setLoadingMessage('Retrying your request');
    }
  };

  const sendClearCommand = () => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN && !isProcessing) {
      // Send /clear command to agent
      wsRef.current.send(JSON.stringify({
        type: 'user_message',
        content: '/clear',
        user_id: user
      }));
      setIsProcessing(true);
      setIsLoading(true);
      setLoadingMessage('Clearing session...');
    }
  };

  const sendCompactCommand = () => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN && !isProcessing) {
      // Send /compact command to agent
      wsRef.current.send(JSON.stringify({
        type: 'user_message',
        content: '/compact',
        user_id: user
      }));
      setIsProcessing(true);
      setIsLoading(true);
      setLoadingMessage('Compacting session...');
    }
  };

  const sendExploreCommand = (command) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN && !isProcessing) {
      // Send /explore_ds command to agent
      wsRef.current.send(JSON.stringify({
        type: 'user_message',
        content: command,
        user_id: user
      }));

      // Add user message to UI immediately
      setMessages(prev => [...prev, {
        id: Date.now(),
        type: 'user',
        content: command,
        timestamp: new Date().toISOString()
      }]);

      setIsProcessing(true);
      setIsLoading(true);
      setLoadingMessage('Exploring data sources...');
      scrollToBottom();
    }
  };

  const downloadFile = async (filename) => {
    try {
      setIsLoading(true);
      setLoadingMessage('Downloading file');
      
      // Get Clerk session token for authentication
      const sessionToken = await clerk.session.getToken();
      if (!sessionToken) {
        throw new Error('Unable to get authentication token');
      }
      
      // Get organization-specific API URL
      const orgSlug = getOrgSlug(organization);
      const apiBaseUrl = getApiBaseUrl(orgSlug);
      
      const response = await fetch(`${apiBaseUrl}/sessions/current/files/${filename}`, {
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
        },
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        setIsLoading(false);
        setLoadingMessage('');
      } else {
        throw new Error('File not found');
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      setMessages(prev => [...prev, {
        id: Date.now(),
        type: 'error',
        content: `Error downloading file: ${error.message}`,
        timestamp: new Date().toISOString()
      }]);
      setIsLoading(false);
      setLoadingMessage('');
    }
  };

  const openSaveReportDialog = () => {
    if (messages.length === 0) {
      setMessages(prev => [...prev, {
        id: Date.now(),
        type: 'error',
        content: 'No messages to save. Start a conversation first.',
        timestamp: new Date().toISOString()
      }]);
      return;
    }
    setMessageToConvert(null); // Save entire conversation
    setShowSaveDialog(true);
  };

  const handleConvertMessageToReport = (message) => {
    setMessageToConvert(message);
    setShowSaveDialog(true);
  };

  const handleAddToChat = ({ filename, selections }) => {
    // Format the context string for multiple selections
    const selectionsText = selections
      .map(sel => `${sel.columnName} = "${sel.value}"`)
      .join(', ');
    const contextText = `[From ${filename}: ${selectionsText}]`;

    // Append to existing input or set as new input
    setInputValue(prev => {
      if (prev.trim()) {
        return `${prev} ${contextText}`;
      }
      return contextText + ' ';
    });
  };

  const confirmSaveReport = async (saveOptions) => {
    const { reportName, complexity, outputFormats, customInstructions } = saveOptions;

    if (!reportName.trim()) {
      setMessages(prev => [...prev, {
        id: Date.now(),
        type: 'error',
        content: 'Please enter a name for the report.',
        timestamp: new Date().toISOString()
      }]);
      return;
    }

    try {
      const trimmedReportName = reportName.trim();

      // Build description (for display purposes only)
      const descriptionParts = [];
      descriptionParts.push(`Complexity: ${complexity}`);

      const selectedFormats = Object.keys(outputFormats).filter(key => outputFormats[key]);
      if (selectedFormats.length > 0) {
        descriptionParts.push(`Output formats: ${selectedFormats.join(', ')}`);
      }

      if (customInstructions) {
        descriptionParts.push(`Instructions: ${customInstructions}`);
      }

      const description = descriptionParts.join(' | ');

      // Call the background save API endpoint (no chat interaction)
      const apiBaseUrl = getApiBaseUrl();
      const workspaceId = typeof selectedWorkspace === 'string'
        ? selectedWorkspace
        : selectedWorkspace?.workspace_id;

      if (!workspaceId) {
        throw new Error('No workspace selected');
      }

      const queryParams = new URLSearchParams({
        workspace_id: workspaceId
      }).toString();

      // Send structured options to backend
      const formData = new FormData();
      formData.append('report_name', trimmedReportName);
      formData.append('description', description);
      formData.append('python_script', '');
      formData.append('complexity', complexity);
      formData.append('output_formats', JSON.stringify(selectedFormats));
      formData.append('custom_instructions', customInstructions || '');

      const response = await fetch(
        `${apiBaseUrl}/saved-reports/save-background?${queryParams}`,
        {
          method: 'POST',
          body: formData,
          headers: {
            'Authorization': `Bearer ${await clerk.session.getToken()}`
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to queue report save');
      }

      const result = await response.json();

      // Add success message to chat
      const successMessage = {
        id: Date.now(),
        type: 'success',
        content: `Report "${trimmedReportName}" is being generated. Check the progress modal for details.`,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, successMessage]);

      // Close modal and clear form
      setShowSaveDialog(false);
      setMessageToConvert(null);

      // Set active generation job
      setActiveGenerationJob({
        jobId: result.job_id,
        reportName: trimmedReportName
      });

      // Trigger opening ReportGenerationProgressModal with the job info
      // This allows user to see the save progress in real-time
      if (onSaveReport) {
        onSaveReport({
          jobId: result.job_id,
          reportName: trimmedReportName
        });
      }

    } catch (error) {
      console.error('Error saving report:', error);
      setMessages(prev => [...prev, {
        id: Date.now(),
        type: 'error',
        content: `❌ **Error saving report:** ${error.message}`,
        timestamp: new Date().toISOString()
      }]);
    }
  };

  const cancelSaveReport = () => {
    setShowSaveDialog(false);
    setMessageToConvert(null);
  };

  return (
    <div className="chat-container">
      {!selectedWorkspace && (
        <div className="no-template-message">
          <div className="no-template-content">
            <div className="no-template-icon">📦</div>
            <div className="no-template-text">
              <div className="no-template-title">Select a Workspace</div>
              <div className="no-template-subtitle">
                Choose a workspace from the header to start chatting
              </div>
            </div>
          </div>
        </div>
      )}
      
      <MessageList
        messages={messages}
        onDownloadFile={downloadFile}
        onRetry={handleRetry}
        onConvertToReport={handleConvertMessageToReport}
        onSaveReport={openSaveReportDialog}
        messagesEndRef={messagesEndRef}
        workspaceId={selectedWorkspace?.workspace_id}
        getAuthToken={getAuthToken}
        onAddToChat={handleAddToChat}
      />
      
      <LoadingIndicator
        isLoading={isLoading || isProcessing}
        statusMessage={loadingMessage || (isProcessing ? 'Processing...' : '')}
        onCancel={isProcessing ? handleInterrupt : null}
      />
      
      <div className="input-container">
        {/* Selected Reference Files */}
        {selectedReferenceFiles && selectedReferenceFiles.length > 0 && (
          <div className="selected-files-bar">
            <div className="selected-files-label">Referenced files:</div>
            <div className="selected-files-list">
              {selectedReferenceFiles.map((file) => (
                <div key={file.file_id} className="selected-file-chip">
                  <FileText size={12} />
                  <span className="selected-file-name" title={file.filename}>
                    {file.filename}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="input-bar">
          <textarea
            ref={fileInputRef}
            className="message-input"
            value={inputValue}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            placeholder={isConnected ? "Type your message..." : "Connecting..."}
            disabled={!isConnected || isProcessing}
            rows={1}
          />

          {isProcessing ? (
            <button
              className="interrupt-button"
              onClick={handleInterrupt}
              title="Interrupt current task"
            >
              <Square size={16} />
            </button>
          ) : (
            <button
              className="send-button"
              onClick={sendMessage}
              disabled={!inputValue.trim() || !isConnected || isProcessing}
              title="Send message"
            >
              <Send size={16} />
            </button>
          )}
        </div>
      </div>
      
      {/* Save Report Modal */}
      <SaveReportModal
        isOpen={showSaveDialog}
        onClose={cancelSaveReport}
        onSave={confirmSaveReport}
        messageToConvert={messageToConvert}
        messages={messages}
        hasActiveJob={!!activeGenerationJob}
        activeJobName={activeGenerationJob?.reportName}
        selectedWorkspace={selectedWorkspace}
      />
    </div>
  );
});

ChatInterface.displayName = 'ChatInterface';

export default ChatInterface;
